<template>
  <div class="detail">
    <section v-if="loading" class="card top skeleton-asset" role="status" aria-live="polite">
      <span class="sr-only">Cargando detalle...</span>
      <div class="asset-header">
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line" style="width: 80px"></div>
          <div class="skeleton skeleton-line xl" style="width: 140px"></div>
          <div class="skeleton skeleton-line" style="width: 90px; height: 22px"></div>
        </div>
        <div class="metrics">
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 70%"></div>
            <div class="skeleton skeleton-line lg" style="width: 80%"></div>
          </div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 62%"></div>
            <div class="skeleton skeleton-line lg" style="width: 76%"></div>
          </div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 58%"></div>
            <div class="skeleton skeleton-line lg" style="width: 72%"></div>
          </div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 64%"></div>
            <div class="skeleton skeleton-line lg" style="width: 78%"></div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="card skeleton-asset-info" aria-hidden="true">
      <div class="asset-info">
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line" style="width: 48%"></div>
          <div class="skeleton skeleton-line lg" style="width: 70%"></div>
        </div>
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line" style="width: 52%"></div>
          <div class="skeleton skeleton-line lg" style="width: 68%"></div>
        </div>
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line" style="width: 44%"></div>
          <div class="skeleton skeleton-line lg" style="width: 64%"></div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="card chart-card skeleton-asset-chart" aria-hidden="true">
      <div class="card-header">
        <div>
          <div class="skeleton skeleton-line lg" style="width: 38%"></div>
          <div class="skeleton skeleton-line" style="width: 52%"></div>
        </div>
      </div>
      <div class="chart-grid">
        <div class="chart-panel">
          <div class="skeleton skeleton-line" style="width: 60%"></div>
          <div class="skeleton skeleton-block" style="height: 180px; width: 100%"></div>
          <div class="skeleton skeleton-line" style="width: 40%"></div>
        </div>
        <div class="chart-panel">
          <div class="skeleton skeleton-line" style="width: 58%"></div>
          <div class="skeleton skeleton-block" style="height: 180px; width: 100%"></div>
          <div class="skeleton skeleton-line" style="width: 46%"></div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="card table-card skeleton-asset-table" aria-hidden="true">
      <div class="card-header">
        <div>
          <div class="skeleton skeleton-line lg" style="width: 28%"></div>
          <div class="skeleton skeleton-line" style="width: 46%"></div>
        </div>
      </div>
      <div class="skeleton-table-rows">
        <div class="skeleton skeleton-line" style="width: 100%"></div>
        <div class="skeleton skeleton-line" style="width: 96%"></div>
        <div class="skeleton skeleton-line" style="width: 98%"></div>
        <div class="skeleton skeleton-line" style="width: 94%"></div>
        <div class="skeleton skeleton-line" style="width: 97%"></div>
      </div>
    </section>

    <section class="card top" v-if="asset">
      <div class="asset-header">
        <div>
          <p class="muted">Asset</p>
          <h2>{{ asset.symbol }}</h2>
          <span class="chip">{{ groupLabel(asset.type) }}</span>
        </div>
        <div class="metrics">
          <div>
            <p>Neto invertido</p>
            <h3>{{ formatEur(netInvestedEur) }}</h3>
          </div>
          <div>
            <p>Valor actual</p>
            <h3>{{ asset.balance_eur != null ? formatEur(asset.balance_eur) : "Sin precio" }}</h3>
          </div>
          <div>
            <p>Comisiones</p>
            <h3>{{ formatEur(totalFeesEur) }}</h3>
          </div>
          <div>
            <p>Resultado</p>
            <h3 :class="profitClass(profitLossPct)">
              {{ formatPct(profitLossPct) }}
            </h3>
          </div>
        </div>
      </div>
    </section>

    <section class="card" v-if="asset">
      <div class="asset-info">
        <div>
          <p class="muted">Cantidad</p>
          <strong>{{ formatAmount(asset.amount) }}</strong>
        </div>
        <div>
          <p class="muted">Precio actual</p>
          <strong>{{ asset.current_price != null ? formatEur(asset.current_price) : "Sin precio" }}</strong>
        </div>
        <div>
          <p class="muted">Tipo</p>
          <strong>{{ groupLabel(asset.type) }}</strong>
        </div>
      </div>
    </section>

    <section class="card chart-card" v-if="asset">
      <div class="card-header">
        <h2>Actividad del asset</h2>
        <p class="muted">Últimas operaciones</p>
      </div>
      <div class="chart-grid">
          <div class="chart-panel" ref="barPanel">
            <h3>Volumen EUR por operación</h3>
            <div class="bar-chart" @mousemove="onBarHover" @mouseleave="hideTooltip('bar')">
              <div
                v-for="(bar, index) in tradeBars.bars"
                :key="bar.key"
                class="bar-col"
                :class="{ 'is-max': bar.isMax, 'is-min': bar.isMin, active: index === barHoverIndex }"
                :title="`${bar.label} · ${bar.type}`"
              >
                <div v-if="bar.isMax" class="bar-label max">
                  <span>Max {{ formatEur(bar.value) }}</span>
                  <small>{{ bar.shortDate }}</small>
                </div>
                <div class="bar" :class="bar.color" :style="{ height: barHeight(bar.value) }"></div>
                <div v-if="bar.isMin" class="bar-label min">
                  <span>Min {{ formatEur(bar.value) }}</span>
                  <small>{{ bar.shortDate }}</small>
                </div>
              </div>
            </div>
            <div v-if="barTooltip.show" class="tooltip" :style="tooltipStyle(barTooltip)">
              <strong>{{ barTooltip.title }}</strong>
              <div v-for="(line, idx) in barTooltip.lines" :key="`bar-tip-${idx}`">
                {{ line }}
              </div>
            </div>
            <div class="chart-legend">
              <span class="legend-item buy">BUY</span>
              <span class="legend-item sell">SELL</span>
              <span class="legend-item transfer">TRANSFER</span>
            </div>
          </div>
          <div class="chart-panel" ref="linePanel">
            <h3>Acumulado de unidades</h3>
            <svg
              class="line-chart"
              ref="lineChart"
              v-if="cumulativeChart.points"
              :viewBox="`0 0 ${cumulativeChart.width} ${cumulativeChart.height}`"
              @mousemove="onLineHover"
              @mouseleave="hideTooltip('line')"
            >
              <polyline :points="cumulativeChart.points" />
              <g v-for="(point, idx) in cumulativeChart.pointsMeta" :key="`pt-${idx}`">
                <circle
                  class="line-point"
                  :class="{ active: idx === lineHoverIndex }"
                  :cx="point.x"
                  :cy="point.y"
                  r="4"
                />
              </g>
              <g v-for="marker in cumulativeChart.markers" :key="marker.kind" class="line-marker">
                <circle
                  :cx="marker.x"
                  :cy="marker.y"
                  r="4"
                  :class="marker.kind"
                />
                <text
                  :x="marker.x"
                  :y="marker.y"
                  :class="marker.kind"
                  text-anchor="middle"
                  :dy="marker.kind === 'max' ? -10 : 16"
                >
                  {{ marker.label }}
                </text>
                <text
                  :x="marker.x"
                  :y="marker.y"
                  :class="marker.kind"
                  text-anchor="middle"
                  :dy="marker.kind === 'max' ? -24 : 30"
                >
                  {{ marker.date }}
                </text>
              </g>
            </svg>
            <div v-else class="chart-empty">Sin datos suficientes</div>
            <div v-if="lineTooltip.show" class="tooltip" :style="tooltipStyle(lineTooltip)">
              <strong>{{ lineTooltip.title }}</strong>
              <div v-for="(line, idx) in lineTooltip.lines" :key="`line-tip-${idx}`">
                {{ line }}
              </div>
            </div>
            <div class="chart-foot">
              <span>Min {{ formatAmount(cumulativeChart.min) }}</span>
              <span>Max {{ formatAmount(cumulativeChart.max) }}</span>
            </div>
          </div>
        </div>
    </section>

    <section class="card table-card" v-if="asset">
      <div class="card-header">
        <h2>Transacciones</h2>
        <p class="muted">Ordenadas por fecha (más reciente primero)</p>
      </div>
      <div class="table">
        <div class="row head">
          <span>Fecha</span>
          <span>Tipo</span>
          <span>Cantidad</span>
          <span>EUR</span>
          <span>Comisión</span>
        </div>
        <div v-for="tx in normalizedTransactions" :key="tx.key" class="row">
          <span>{{ tx.date }}</span>
          <span>{{ tx.type }}</span>
          <span>{{ tx.amount }}</span>
          <span>{{ tx.eur }}</span>
          <span>{{ tx.fee }}</span>
        </div>
      </div>
    </section>

    <section v-if="error" class="state error">{{ error }}</section>
  </div>
</template>

<script>
import { fetchBalanceAsset } from "../services/api";

export default {
  name: "AssetDetailView",
  props: {
    symbol: { type: String, default: "" },
  },
  data() {
    return {
      asset: null,
      loading: false,
      error: "",
      barTooltip: { show: false, x: 0, y: 0, title: "", lines: [] },
      lineTooltip: { show: false, x: 0, y: 0, title: "", lines: [] },
      barHoverIndex: null,
      lineHoverIndex: null,
    };
  },
  computed: {
    netInvestedEur() {
      if (!this.asset) return null;
      const trades = Array.isArray(this.asset.trades) ? this.asset.trades : [];
      const buy = trades
        .filter((trade) => trade.type === "BUY")
        .reduce((sum, trade) => sum + Number(trade.amount_eur || 0), 0);
      const sell = trades
        .filter((trade) => trade.type === "SELL")
        .reduce((sum, trade) => sum + Number(trade.amount_eur || 0), 0);
      return buy - sell;
    },
    totalFeesEur() {
      if (!this.asset) return null;
      const trades = Array.isArray(this.asset.trades) ? this.asset.trades : [];
      return trades.reduce((sum, trade) => sum + Number(trade.amount_fee || 0), 0);
    },
    profitLossPct() {
      if (!this.asset) return null;
      const current = this.asset.balance_eur;
      const invested = this.netInvestedEur;
      if (current == null || invested == null || invested <= 0) return null;
      return ((Number(current) - Number(invested)) / Number(invested)) * 100;
    },
    tradeBars() {
      if (!this.asset?.trades?.length) {
        return { bars: [], max: 0 };
      }
      const recent = [...this.asset.trades]
        .sort((a, b) => Number(a.unix_timestamp || 0) - Number(b.unix_timestamp || 0))
        .slice(-24);
      const bars = recent.map((trade, idx) => {
        const value = Math.abs(Number(trade.amount_eur || 0));
        const type = String(trade.type || "TRANSFER").toUpperCase();
        const color = type === "BUY" ? "buy" : type === "SELL" ? "sell" : "transfer";
        return {
          key: `bar-${idx}-${trade.unix_timestamp}`,
          value,
          type,
          color,
          label: this.formatUnix(trade.unix_timestamp),
          shortDate: this.formatUnixShort(trade.unix_timestamp),
          unix: trade.unix_timestamp,
          amountAsset: trade.amount_asset,
          fee: trade.amount_fee,
        };
      });
      const max = Math.max(...bars.map((bar) => bar.value), 0);
      const min = Math.min(...bars.map((bar) => bar.value), 0);
      const maxIndex = bars.findIndex((bar) => bar.value === max);
      const minIndex = bars.findIndex((bar) => bar.value === min);
      const decorated = bars.map((bar, idx) => ({
        ...bar,
        isMax: idx === maxIndex && bars.length > 0,
        isMin: idx === minIndex && bars.length > 1,
      }));
      return { bars: decorated, max, min };
    },
    cumulativeChart() {
      const width = 320;
      const height = 140;
      const padding = 10;
      if (!this.asset?.trades?.length) {
        return { points: "", min: 0, max: 0, width, height, markers: [], pointsMeta: [] };
      }
      const sorted = [...this.asset.trades].sort(
        (a, b) => Number(a.unix_timestamp || 0) - Number(b.unix_timestamp || 0)
      );
      const timestamps = sorted.map((trade) => Number(trade.unix_timestamp || 0));
      let cumulative = 0;
      const values = sorted.map((trade) => {
        const amount = Number(trade.amount_asset || 0);
        const type = String(trade.type || "TRANSFER").toUpperCase();
        if (type === "BUY") {
          cumulative += amount;
        } else if (type === "SELL") {
          cumulative -= amount;
        }
        return cumulative;
      });
      if (values.length < 2) {
        return { points: "", min: 0, max: 0, width, height, markers: [], pointsMeta: [] };
      }
      const min = Math.min(...values, 0);
      const max = Math.max(...values, 0);
      const range = max - min || 1;
      const step = (width - padding * 2) / (values.length - 1);
      const pointFor = (index) => {
        const x = padding + index * step;
        const y =
          height -
          padding -
          ((values[index] - min) / range) * (height - padding * 2);
        return { x, y };
      };
      const points = values
        .map((value, index) => {
          const { x, y } = pointFor(index);
          return `${x},${y}`;
        })
        .join(" ");
      const pointsMeta = values.map((value, index) => {
        const { x, y } = pointFor(index);
        return {
          x,
          y,
          value,
          unix: timestamps[index],
        };
      });
      const maxIndex = values.indexOf(max);
      const minIndex = values.indexOf(min);
      const maxPoint = pointFor(maxIndex);
      const minPoint = pointFor(minIndex);
      const markers = [
        {
          kind: "max",
          x: maxPoint.x,
          y: maxPoint.y,
          label: `Max ${this.formatAmount(max)}`,
          date: this.formatUnixShort(timestamps[maxIndex]),
        },
        {
          kind: "min",
          x: minPoint.x,
          y: minPoint.y,
          label: `Min ${this.formatAmount(min)}`,
          date: this.formatUnixShort(timestamps[minIndex]),
        },
      ];
      return { points, min, max, width, height, markers, pointsMeta };
    },
    normalizedTransactions() {
      if (!this.asset?.trades) return [];
      return this.asset.trades
        .map((trade, idx) => {
          const unix = Number(trade.unix_timestamp || 0);
          const dateIso = unix ? new Date(unix * 1000).toISOString() : null;
          return {
            key: `trade-${idx}-${unix}`,
            dateIso,
            date: unix ? this.formatUnix(unix) : "—",
            type: trade.type,
            amount: `${trade.amount_asset} ${this.asset.symbol}`,
            eur: this.formatEur(trade.amount_eur),
            fee: this.formatEur(trade.amount_fee),
          };
        })
        .sort((a, b) => new Date(b.dateIso || 0).getTime() - new Date(a.dateIso || 0).getTime());
    },
  },
  async mounted() {
    await this.loadData();
  },
  watch: {
    symbol() {
      this.loadData();
    },
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = "";
      try {
        const response = await fetchBalanceAsset(this.symbol);
        this.asset = Array.isArray(response) ? response[0] : null;
      } catch (err) {
        this.error = err?.message || "No se pudo cargar el detalle.";
      } finally {
        this.loading = false;
      }
    },
    formatEur(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(Number(value));
    },
    formatPct(value) {
      if (value == null) return "—";
      const number = Number(value);
      const sign = number > 0 ? "+" : "";
      return `${sign}${number.toFixed(2)}%`;
    },
    profitClass(value) {
      if (value == null) return "muted";
      return Number(value) >= 0 ? "positive" : "negative";
    },
    groupLabel(group) {
      const map = {
        CRYPTO: "Cripto",
        METAL: "Metales",
        FIAT: "Fiat",
        STOCK: "Acciones",
        ETF: "ETFs",
        INDEX: "Índices",
        OTHER: "Otros",
      };
      const key = group ? String(group).toUpperCase() : "OTHER";
      return map[key] || group;
    },
    formatUnix(unix) {
      if (!unix) return "—";
      return new Intl.DateTimeFormat("es-ES", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(unix * 1000));
    },
    formatUnixShort(unix) {
      if (!unix) return "—";
      return new Intl.DateTimeFormat("es-ES", {
        dateStyle: "short",
      }).format(new Date(unix * 1000));
    },
    formatAmount(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 8,
      }).format(Number(value));
    },
    barHeight(value) {
      if (!this.tradeBars.max) return "0%";
      return `${Math.max((Number(value) / this.tradeBars.max) * 100, 4)}%`;
    },
    onBarHover(event) {
      const bars = this.tradeBars.bars;
      if (!bars.length) return;
      const container = event.currentTarget;
      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const index = Math.max(
        0,
        Math.min(bars.length - 1, Math.floor((x / rect.width) * bars.length))
      );
      const bar = bars[index];
      this.barHoverIndex = index;
      this.barTooltip = {
        show: true,
        x: (rect.width / bars.length) * (index + 0.5),
        y: 8,
        title: `${bar.type} · ${bar.shortDate}`,
        lines: [
          `EUR ${this.formatEur(bar.value)}`,
          `Comisión ${this.formatEur(bar.fee)}`,
          `${this.formatAmount(bar.amountAsset)} ${this.asset?.symbol || ""}`,
        ],
      };
    },
    onLineHover(event) {
      const points = this.cumulativeChart.pointsMeta || [];
      if (!points.length || !this.$refs.lineChart) return;
      const svgRect = this.$refs.lineChart.getBoundingClientRect();
      const scaleX = svgRect.width / this.cumulativeChart.width;
      const xView = (event.clientX - svgRect.left) / scaleX;
      let nearestIndex = 0;
      let nearestDist = Number.POSITIVE_INFINITY;
      points.forEach((point, idx) => {
        const dist = Math.abs(point.x - xView);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIndex = idx;
        }
      });
      const point = points[nearestIndex];
      this.lineHoverIndex = nearestIndex;
      this.lineTooltip = {
        show: true,
        x: point.x * scaleX,
        y: 8,
        title: "Acumulado",
        lines: [
          `${this.formatAmount(point.value)} ${this.asset?.symbol || ""}`,
          this.formatUnixShort(point.unix),
        ],
      };
    },
    hideTooltip(kind) {
      const tooltipKey = kind === "bar" ? "barTooltip" : "lineTooltip";
      this[tooltipKey] = { ...this[tooltipKey], show: false };
      if (kind === "bar") {
        this.barHoverIndex = null;
      } else {
        this.lineHoverIndex = null;
      }
    },
    tooltipStyle(tooltip) {
      return {
        left: `${tooltip.x + 12}px`,
        top: `${tooltip.y + 12}px`,
      };
    },
  },
};
</script>

<style scoped>
.detail {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow);
}

.top {
  background: linear-gradient(120deg, rgba(34, 197, 94, 0.15), rgba(17, 24, 39, 0.9));
}

.asset-header {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.metrics p {
  margin: 0 0 6px;
  color: var(--muted);
}

.asset-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.chart-card h3 {
  margin: 0 0 12px;
  font-size: 15px;
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.chart-panel {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px;
  min-height: 220px;
  position: relative;
}

.bar-chart {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 140px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  padding-bottom: 8px;
}

.bar-col {
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
  align-items: flex-end;
  align-self: stretch;
}

.bar {
  width: 100%;
  border-radius: 6px 6px 2px 2px;
  min-height: 6px;
}

.bar.buy {
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.9), rgba(34, 197, 94, 0.3));
}

.bar.sell {
  background: linear-gradient(180deg, rgba(248, 113, 113, 0.9), rgba(248, 113, 113, 0.3));
}

.bar.transfer {
  background: linear-gradient(180deg, rgba(148, 163, 184, 0.9), rgba(148, 163, 184, 0.3));
}

.bar-col.is-max .bar {
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.7);
}

.bar-col.is-min .bar {
  box-shadow: 0 0 0 2px rgba(248, 113, 113, 0.7);
}

.bar-col.active .bar {
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.8);
}

.bar-label {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 4px 6px;
  font-size: 11px;
  white-space: nowrap;
  text-align: center;
}

.bar-label small {
  display: block;
  color: var(--muted);
}

.bar-label.max {
  top: -42px;
}

.bar-label.min {
  bottom: -46px;
}

.chart-legend {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  font-size: 12px;
}

.legend-item {
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
}

.legend-item.buy {
  color: var(--accent-3);
  border-color: rgba(34, 197, 94, 0.4);
}

.legend-item.sell {
  color: var(--danger);
  border-color: rgba(248, 113, 113, 0.4);
}

.legend-item.transfer {
  color: var(--muted);
}

.line-chart {
  width: 100%;
  height: 160px;
}

.line-chart polyline {
  fill: none;
  stroke: rgba(56, 189, 248, 0.9);
  stroke-width: 3;
}

.line-point {
  fill: rgba(56, 189, 248, 0.2);
  stroke: rgba(56, 189, 248, 0.6);
  stroke-width: 1.5;
  cursor: pointer;
}

.line-point.active {
  fill: rgba(56, 189, 248, 0.7);
  stroke: rgba(56, 189, 248, 0.9);
}

.line-point:hover {
  fill: rgba(56, 189, 248, 0.6);
}

.line-marker circle {
  fill: #0f172a;
  stroke-width: 2;
}

.line-marker circle.max {
  stroke: rgba(34, 197, 94, 0.9);
}

.line-marker circle.min {
  stroke: rgba(248, 113, 113, 0.9);
}

.line-marker text {
  font-size: 11px;
  fill: var(--text);
}

.line-marker text.max {
  fill: rgba(34, 197, 94, 0.9);
}

.line-marker text.min {
  fill: rgba(248, 113, 113, 0.9);
}

.tooltip {
  position: absolute;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 12px;
  color: var(--text);
  pointer-events: none;
  min-width: 140px;
  z-index: 2;
  box-shadow: var(--shadow);
}

.tooltip strong {
  display: block;
  margin-bottom: 4px;
}

.chart-foot {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--muted);
}

.chart-empty {
  color: var(--muted);
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
}

.chip {
  display: inline-block;
  margin-top: 6px;
  background: rgba(34, 197, 94, 0.16);
  color: var(--accent-3);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.table {
  display: grid;
}

.row {
  display: grid;
  grid-template-columns: 1.3fr 1fr 1.2fr 1fr 1fr;
  gap: 12px;
  padding: 12px 10px;
  border-top: 1px solid var(--border);
}

.row.head {
  border-top: none;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
  color: var(--muted);
}

.muted {
  color: var(--muted);
}

.positive {
  color: var(--accent-3);
}

.negative {
  color: var(--danger);
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.skeleton-asset .asset-header {
  align-items: flex-start;
}

.skeleton-table-rows {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

@media (max-width: 900px) {
  .row {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .row span:nth-child(4),
  .row span:nth-child(5) {
    display: none;
  }

  .chart-grid {
    grid-template-columns: 1fr;
  }
}
</style>
